import { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useRetention } from '@/hooks/useRetention';
import { useRecoveryProof } from '@/hooks/useRecoveryProof';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { trackEvent } from '@/lib/analytics';
import {
  RetentionCategory, CATEGORY_LABEL, CATEGORY_COLOR, CATEGORY_ORDER,
} from '@/lib/retention';
import { RetentionDesktop } from '@/components/RetentionDesktop';
import { NotifyModal } from './_components/NotifyModal';
import { RetentionTrendChart } from './_components/RetentionTrendChart';
import { RetentionTour } from './_components/RetentionTour';

type Filter = RetentionCategory | 'todos';

export default function RetencaoScreen() {
  const { profile } = useAuth();
  const router = useRouter();
  const isDesktop = useIsDesktop();
  const [filter, setFilter] = useState<Filter>('todos');
  const [search, setSearch] = useState('');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const { data, isLoading } = useRetention(profile?.id ?? '');
  const { data: recovery } = useRecoveryProof(profile?.id ?? '');

  if (isDesktop) return <RetentionDesktop />;

  if (isLoading || !data) {
    return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;
  }

  const { kpis, counts, total, students } = data;

  const filteredStudents = students.filter(s => {
    if (filter !== 'todos' && s.category !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = (s.full_name ?? s.email ?? '').toLowerCase();
      if (!name.includes(q)) return false;
    }
    return true;
  });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* Botão "Ver tour novamente" */}
      <TouchableOpacity onPress={() => setTourOpen(true)} style={styles.tourBtn} activeOpacity={0.7}>
        <Ionicons name="help-circle-outline" size={14} color="#C8102E" />
        <Text style={styles.tourBtnText}>Ver tour da Área de Retenção</Text>
      </TouchableOpacity>

      {/* ─── Resumo do mês ─── */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Resumo do mês</Text>
        <View style={styles.kpiGrid}>
          <KpiBox label="Alunos ativos" value={String(kpis.activeStudents)} />
          <KpiBox label="Engajamento" value={String(kpis.engagementRate)} unit="%" />
          <KpiBox label="Crescimento" value={String(kpis.growthRate)} unit="%" highlight={kpis.growthRate > 0} />
          <KpiBox label="Recuperação" value={String(kpis.recoveryRate)} unit="%" highlight={kpis.recoveryRate > 0} />
        </View>
      </View>

      {/* ─── Resultado dos seus toques (loop fechado) ─── */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Resultado dos seus toques</Text>
        {recovery && recovery.notificados > 0 ? (
          <>
            <Text style={styles.cardSub}>Últimos {recovery.janelaDias} dias</Text>
            <View style={styles.recoveryRow}>
              <Text style={styles.recoveryBig}>{recovery.voltaram}</Text>
              <Text style={styles.recoveryOf}>de {recovery.notificados}</Text>
            </View>
            <Text style={styles.recoveryCaption}>alunos que você notificou voltaram a treinar 🎉</Text>
            <View style={styles.recoveryTrack}>
              <View style={[styles.recoveryFill, { width: `${recovery.taxa}%` }]} />
            </View>
          </>
        ) : (
          <Text style={styles.recoveryEmpty}>
            Quando você notificar alunos que sumiram, eu mostro aqui quantos voltaram a treinar depois do seu toque.
          </Text>
        )}
      </View>

      {/* ─── Engajamento dos alunos (barras) ─── */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Engajamento dos alunos</Text>
        <Text style={styles.cardSub}>Classificação baseada na última semana</Text>
        {CATEGORY_ORDER.map(cat => {
          const count = counts[cat];
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <View key={cat} style={styles.barRow}>
              <View style={styles.barLabelRow}>
                <Text style={styles.barLabel}>{CATEGORY_LABEL[cat]}</Text>
                <Text style={styles.barValue}>{count} ({pct}%)</Text>
              </View>
              <View style={styles.barTrack}>
                <View style={[styles.barFill, { width: `${pct}%`, backgroundColor: CATEGORY_COLOR[cat] }]} />
              </View>
            </View>
          );
        })}
      </View>

      {/* ─── Tendência (gráfico) ─── */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Tendência das últimas semanas</Text>
        <Text style={styles.cardSub}>Distribuição de categorias por semana</Text>
        <RetentionTrendChart personalId={profile?.id ?? ''} />
      </View>

      {/* ─── Alunos ─── */}
      <View style={styles.card}>
        <View style={styles.alunosHeader}>
          <Text style={styles.cardTitle}>Alunos</Text>
          <TouchableOpacity
            style={styles.notifyBtn}
            onPress={() => {
              trackEvent('pt_clicked_notify', { filter, count: filteredStudents.length, surface: 'mobile' });
              setNotifyOpen(true);
            }}
            disabled={filteredStudents.length === 0}
            activeOpacity={0.7}
          >
            <Ionicons name="paper-plane-outline" size={14} color="#fff" />
            <Text style={styles.notifyBtnText}>Notificar</Text>
          </TouchableOpacity>
        </View>

        <TextInput
          style={styles.search}
          placeholder="Buscar por nome ou email…"
          placeholderTextColor="#aaa"
          value={search}
          onChangeText={setSearch}
        />

        <View style={styles.filterChips}>
          <Chip label="Todos" active={filter === 'todos'} onPress={() => setFilter('todos')} />
          {CATEGORY_ORDER.map(cat => (
            <Chip
              key={cat}
              label={`${CATEGORY_LABEL[cat]} (${counts[cat]})`}
              active={filter === cat}
              color={CATEGORY_COLOR[cat]}
              onPress={() => setFilter(cat)}
            />
          ))}
        </View>

        {filteredStudents.length === 0 ? (
          <Text style={styles.empty}>
            {students.length === 0
              ? 'Nenhum aluno cadastrado ainda.'
              : 'Nenhum aluno nessa categoria com esse filtro.'}
          </Text>
        ) : (
          filteredStudents.map(s => (
            <TouchableOpacity
              key={s.id}
              style={styles.studentRow}
              onPress={() => router.push(`/(personal)/students/${s.id}` as never)}
              activeOpacity={0.7}
            >
              <View style={styles.studentInfo}>
                <Text style={styles.studentName} numberOfLines={1}>
                  {s.full_name ?? s.email}
                </Text>
                <Text style={styles.studentMeta}>
                  {s.daysSinceLast === null
                    ? 'nunca treinou'
                    : s.daysSinceLast === 0
                    ? 'treinou hoje'
                    : `${s.daysSinceLast} dia${s.daysSinceLast === 1 ? '' : 's'} sem treinar`}
                  {s.sessionsLastWeek > 0 ? ` · ${s.sessionsLastWeek} essa semana` : ''}
                </Text>
              </View>
              <View style={[styles.categoryBadge, { backgroundColor: CATEGORY_COLOR[s.category] + '22' }]}>
                <Text style={[styles.categoryBadgeText, { color: CATEGORY_COLOR[s.category] }]}>
                  {CATEGORY_LABEL[s.category]}
                </Text>
              </View>
            </TouchableOpacity>
          ))
        )}
      </View>

      {notifyOpen && (
        <NotifyModal
          visible={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          personalId={profile?.id ?? ''}
          preselected={filteredStudents}
          initialCategory={filter}
        />
      )}

      <RetentionTour forceOpen={tourOpen} onFinish={() => setTourOpen(false)} />
    </ScrollView>
  );
}

// ─── Sub-componentes ───────────────────────────────────────────────────────

function KpiBox({
  label, value, unit, highlight = false,
}: { label: string; value: string; unit?: string; highlight?: boolean }) {
  return (
    <View style={styles.kpiBox}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={[styles.kpiValue, highlight && styles.kpiValueHighlight]}>
        {value}{unit && <Text style={styles.kpiUnit}>{unit}</Text>}
      </Text>
    </View>
  );
}

function Chip({
  label, active, color, onPress,
}: { label: string; active: boolean; color?: string; onPress: () => void }) {
  const bg = active ? (color ?? '#C8102E') : '#EEE';
  const fg = active ? '#fff' : '#555';
  return (
    <TouchableOpacity
      style={[styles.chip, { backgroundColor: bg }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[styles.chipText, { color: fg }]}>{label}</Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 16, paddingBottom: 40 },

  card: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12 },
  cardTitle: { fontSize: 16, fontWeight: '700', color: '#1A1A1A' },
  cardSub: { fontSize: 12, color: '#888', marginTop: 2, marginBottom: 12 },

  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  kpiBox: {
    flexBasis: '47%', flexGrow: 1,
    backgroundColor: '#F9F9F9', borderRadius: 10, padding: 12,
    borderWidth: 1, borderColor: '#EEE',
  },
  kpiLabel: {
    fontSize: 10, color: '#888', fontWeight: '700', letterSpacing: 0.5,
    marginBottom: 4, textTransform: 'uppercase',
  },
  kpiValue: { fontSize: 24, fontWeight: '800', color: '#1A1A1A' },
  kpiValueHighlight: { color: '#2E7D32' },
  kpiUnit: { fontSize: 14, fontWeight: '500', color: '#888' },

  barRow: { marginBottom: 10 },
  barLabelRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  barLabel: { fontSize: 13, color: '#1A1A1A', fontWeight: '600' },
  barValue: { fontSize: 12, color: '#555' },
  barTrack: { height: 6, backgroundColor: '#EEE', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: 6, borderRadius: 3 },

  search: {
    backgroundColor: '#F9F9F9', borderRadius: 8, padding: 10, fontSize: 14,
    marginTop: 10, marginBottom: 10, borderWidth: 1, borderColor: '#EEE', color: '#1A1A1A',
  },
  filterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 },
  chipText: { fontSize: 12, fontWeight: '600' },

  studentRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F0F0F0',
  },
  studentInfo: { flex: 1 },
  studentName: { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  studentMeta: { fontSize: 11, color: '#888', marginTop: 2 },
  categoryBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  categoryBadgeText: { fontSize: 11, fontWeight: '700' },

  empty: { fontSize: 13, color: '#888', textAlign: 'center', paddingVertical: 20 },

  alunosHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  notifyBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#C8102E', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8,
  },
  notifyBtnText: { color: '#fff', fontWeight: '700', fontSize: 12 },

  tourBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 10,
    backgroundColor: '#FDEDEF', borderRadius: 6, marginBottom: 12,
  },
  tourBtnText: { color: '#C8102E', fontSize: 12, fontWeight: '600' },

  recoveryRow: { flexDirection: 'row', alignItems: 'baseline', gap: 8, marginTop: 6 },
  recoveryBig: { fontSize: 34, fontWeight: '800', color: '#2E7D32' },
  recoveryOf: { fontSize: 15, color: '#888', fontWeight: '600' },
  recoveryCaption: { fontSize: 13, color: '#1A1A1A', marginTop: 2 },
  recoveryTrack: { height: 8, backgroundColor: '#EEE', borderRadius: 4, overflow: 'hidden', marginTop: 12 },
  recoveryFill: { height: 8, borderRadius: 4, backgroundColor: '#2E7D32' },
  recoveryEmpty: { fontSize: 13, color: '#888', marginTop: 8, lineHeight: 19 },
});
